/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import {
  Row,
  Col,
  Radio,
} from 'antd';

import TitleRow from './TitleRow';
import OptionalRow from './OptionalRow';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import HoverHelp from '../../../common/HoverHelp';

import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import FieldTriggerFlag from './FieldTriggerFlag';
import ConditionalRenderingRow from './ConditionalRenderingRow';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import OnTraccrRadioGroup from '../../../common/inputs/OnTraccrRadioGroup';
import FieldWidthSelector from './FieldWidthSelector';
import HideIfEmptyInPDFCheckbox from './HideIfEmptyInPDFCheckbox';

const configure = ({
  setConfigProps,
  configProps = {},
  sections = [],
  isExternalForm,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  id,
  formTemplates = [],
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  labels = [],
  contactAddressBooks = {},
  buckets = [],
  scheduleCategories = [],
  isV3Form = false,
} = {}) => {
  const {
    optional,
    explain = [],
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    hideNA = false,
  } = configProps;

  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      title: value,
    });
  };

  const setExplain = (key) => (checked) => {
    let newExplain = [...explain];
    if (checked) {
      newExplain.push(key);
    } else {
      newExplain = newExplain.filter((item) => item !== key);
    }
    setConfigProps({
      ...configProps,
      explain: newExplain,
    });
  };

  const setOptional = (checked) => {
    setConfigProps({
      ...configProps,
      optional: checked,
    });
  };

  const setConditionalRenderingFormula = (formula) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: formula,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };

  const setFieldTrigger = (checked) => {
    setConfigProps({
      ...configProps,
      fieldTrigger: checked,
    });
  };

  const setHide = (checked) => {
    setConfigProps({
      ...configProps,
      hideNA: checked,
    });
  };

  const setWidth = (value) => {
    setConfigProps({
      ...configProps,
      width: value,
    });
  };

  return (
    <div>
      <Row className="form-required-field">
        Title:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <OptionalRow onChange={setOptional} optional={optional} disabled={disableOptional} />
      <ConditionalRenderingRow
        onChange={setHasConditionalRendering}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
        sections={sections}
        id={id}
        users={users}
        customers={customers}
        projects={projects}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        value={hideNA}
        onChange={setHide}
        label={'Hide \'N/A\' option'}
      />
      <Row style={{ marginTop: 20 }} gutter={10}>
        <Col>
          Requires Explanation:
        </Col>
        <Col>
          <HoverHelp placement="top" content="Select which options require a user explanation" />
        </Col>
      </Row>
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        value={explain.includes('yes')}
        onChange={setExplain('yes')}
        label={'Require explanation for \'Yes\' answers'}
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        value={explain.includes('no')}
        onChange={setExplain('no')}
        label={'Require explanation for \'No\' answers'}
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        value={explain.includes('n/a')}
        onChange={setExplain('n/a')}
        label={'Require explanation for \'N/A\' answers'}
      />
      <FieldWidthSelector
        value={configProps.width}
        onChange={setWidth}
      />
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={setFieldTrigger}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
    </div>
  );
};

const convertToDisplay = (val) => {
  if (val === 'yes') return 'Yes';
  if (val === 'no') return 'No';
  if (val === 'n/a') return 'N/A';
  return val;
};

const preview = ({
  setPreviewProps,
  configProps = {},
  previewProps = {},
  id,
  responses = {},
  setResponses,
  responding = false,
  sections,
  projectId,
  templateId,
  divisions,
  isDisplay,
  onFieldTrigger,
  isFormBuilder,
} = {}) => {
  const isFieldDisplay = !isFormBuilder && isDisplay;
  const {
    optional,
    title = 'Title goes here',
    explain = [],
    fieldTrigger,
    hideNA = false,
  } = configProps;

  const { selected, explanation } = previewProps;

  const {
    [id]: {
      value: respSelected,
      explanation: respExplanation = '',
    } = {},
  } = responding ? responses : {};

  const actualSelected = responding ? respSelected : selected;
  const explanationValue = responding ? respExplanation : explanation;

  const setSelected = (value) => {
    let newExplanationValue = explanationValue;

    // Clear value if new selection does not require explanation
    if (!explain.includes(value)) {
      newExplanationValue = null;
    }

    if (responding) {
      const newResp = {
        [id]: {
          ...(responses[id]),
          value,
          explanation: newExplanationValue,
        },
      };
      setResponses({
        ...responses,
        ...newResp,
      });
    } else {
      if (!setPreviewProps) return;
      setPreviewProps({
        ...previewProps,
        selected: value,
        explanation: newExplanationValue,
      });
    }
  };
  const onTextChange = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    if (responding) {
      setResponses({
        ...responses,
        [id]: {
          ...(responses[id]),
          explanation: value,
        },
      });
    } else if (!setPreviewProps) return;
    setPreviewProps({
      ...previewProps,
      explanation: value,
    });
  };

  const body = isFieldDisplay ? (
    <div className="form-field-display-value-text">
      {convertToDisplay(actualSelected)}
    </div>
  ) : (
    <OnTraccrRadioGroup onChange={setSelected} value={actualSelected}>
      <Radio value="yes">Yes</Radio>
      <Radio value="no">No</Radio>
      {!hideNA && (
        <Radio value="n/a">N/A</Radio>
      )}
    </OnTraccrRadioGroup>
  );

  const explanationNode = isDisplay
    ? (
      <div className="form-field-display-value-text">{explanationValue}</div>
    )
    : (
      <OnTraccrTextInput
        style={{ pointerEvents: 'auto' }}
        placeholder="Enter additional details"
        textarea
        value={explanationValue}
        onChange={onTextChange}
        readOnly={isDisplay}
      />
    );

  return (
    <div style={{ position: 'relative' }}>
      <TitleRow
        isFieldDisplay={isFieldDisplay}
        title={title}
        optional={optional}
        filter={
          fieldTrigger && !isDisplay
            ? (
              <FieldTriggerFlag
                sections={sections}
                templateId={templateId}
                projectId={projectId}
                divisions={divisions}
                configProps={configProps}
                responding={responding}
                onFieldTrigger={onFieldTrigger}
              />
            ) : null
        }
      />
      <Row style={{ marginTop: 10, pointerEvents: isDisplay ? 'none' : 'auto' }}>
        {body}
      </Row>
      {
        !!actualSelected && explain.includes(actualSelected)
        && (
        <Row style={{ marginTop: 15 }}>
          {explanationNode}
        </Row>
        )
      }
    </div>
  );
};

export default {
  configure,
  preview,
  title: 'Yes/No',
  description: 'User can select Yes or No. You can also require the user to enter an explanation',
};
